import {GROUP_TYPE} from './canvas_timeline_util';
import {interactionManager} from './canvas_timeline_interaction_manager';
import * as tracking from '../../../tracking';

class Group {
	constructor(groupType, data) {
		this.data = data;
		this.groupType = groupType;
		this.itemRowHeight = this.getItemRowHeightFromGroupType(groupType);
		this.expansionMarginBottom = this.getExpansionMarginBottomFromGroupType(groupType);
		this.height = this.itemRowHeight;
		this.totalHeight = this.height;
		this.marginX = data.marginX | 0;
		this.expanded = false;
		this.id = data.id;
		this.groups = data.groups || [];
		this.itemRowCount = 1;
		this.items = [];
		this.onItemCreate = data.onItemCreate;
		this.composeItems = data.composeItems;
		this.filterGroup = data.filterGroup;
		this.hideIfEmpty = data.hideIfEmpty;
		this.showOnlyIfNoSiblingsVisible = data.showOnlyIfNoSiblingsVisible;
		this.drawIfAnyChildVisible = data.drawIfAnyChildVisible;
		this.dragGroupId = data.dragGroupId;
		this.droppableItemTypes = data.droppableItemTypes || [];
		this.onMouseEnter = data.onMouseEnter;
		this.onMouseLeave = data.onMouseLeave;
		this.onClick = data.onClick;
		this.preventExpansion = data.preventExpansion;
		this.onExpand = data.onExpand;
		this.hideCondition = data.hideCondition;
		this.filtered = data.filtered || false;
		this.forceDrawCondition = data.forceDrawCondition;
		this.renderRowLines = data.renderRowLines;
		this.hideIfAllChildrenAreFiltered = data.hideIfAllChildrenAreFiltered;
		this.includeInRowHover = true;
	}

	getItemRowHeightFromGroupType(groupType) {
		switch (groupType) {
			case GROUP_TYPE.PEOPLE_SCHEDULING_PERSON:
				return 48;
			case GROUP_TYPE.PEOPLE_SCHEDULING_PROJECT:
				return 42;
			case GROUP_TYPE.PEOPLE_SCHEDULING_NON_PROJECT_TIME:
				return 40;
			case GROUP_TYPE.PEOPLE_SCHEDULING_NON_PROJECT_TIME_TIME_REGISTRATION:
				return 48;
			case GROUP_TYPE.PEOPLE_SCHEDULING_TASK:
				return 48;
			case GROUP_TYPE.PEOPLE_SCHEDULING_PLACEHOLDER:
				return 48;
			case GROUP_TYPE.PEOPLE_SCHEDULING_UNASSIGNED_ROLE:
				return 48;
			case GROUP_TYPE.PEOPLE_SCHEDULING_UNASSIGNED_ROLE_TASK:
				return 48;
			case GROUP_TYPE.PROJECT_SCHEDULING_PROJECT:
				return 42;
			case GROUP_TYPE.PROJECT_SCHEDULING_PHASE:
				return 36;
			case GROUP_TYPE.PROJECT_SCHEDULING_PERSON:
				return 36;
			case GROUP_TYPE.PROJECT_TIMELINE_HEATMAP_PERSON:
				return 47;
			case GROUP_TYPE.PROJECT_SCHEDULING_TASK:
				return 28;
			case GROUP_TYPE.PROJECT_SCHEDULING_PROJECT_SUB_GROUP:
				return 40;
			case GROUP_TYPE.PROJECT_SCHEDULING_PROJECT_GROUP:
				return 42;
			case GROUP_TYPE.PROJECT_SCHEDULING_PLACEHOLDER:
				return 28;
			case GROUP_TYPE.PEOPLE_SCHEDULING_COMPANY_UTILIZATION:
				return 48;
			case GROUP_TYPE.PEOPLE_SCHEDULING_PERSON_GROUP:
				return 48;
			case GROUP_TYPE.PEOPLE_SCHEDULING_NEW_PERSON:
				return 48;
			case GROUP_TYPE.PEOPLE_SCHEDULING_PERSON_TASKS:
				return 48;
			case GROUP_TYPE.PEOPLE_SCHEDULING_PERSON_ALLOCATIONS:
				return 48;
			case GROUP_TYPE.DATE_RANGE_GROUP:
				return 0;
			case GROUP_TYPE.PROJECT_SCHEDULING_LOADING:
				return 28;
			case GROUP_TYPE.PEOPLE_SCHEDULING_LOADING:
				return 28;
			default:
				// eslint-disable-next-line no-console
				console.warn('Unrecognized group type. >' + groupType + '< Using default height of 40px. Check getItemRowHeightFromGroupType function in canvas_timeline_group.');
				return 40;
		}
	}

	refreshData(updatedData) {}

	getExpansionMarginBottomFromGroupType(groupType) {
		switch (groupType) {
			case GROUP_TYPE.PROJECT_SCHEDULING_PROJECT:
				return 9;
			case GROUP_TYPE.PROJECT_SCHEDULING_PROJECT_GROUP:
				return 9;
			default:
				return 0;
		}
	}

	toggleExpansion() {
		this.setExpanded(!this.expanded);
	}

	setExpanded(expanded) {
		switch (this.groupType) {
			case 0:
				tracking.trackEvent((expanded ? 'Expanding' : 'Collapsing') + ' People Scheduling, Person');
				break;
			case 8:
				tracking.trackEvent((expanded ? 'Expanding' : 'Collapsing') + ' Project Scheduling, Project');
				break;
			case 9:
				tracking.trackEvent((expanded ? 'Expanding' : 'Collapsing') + ' Project Scheduling, Milestone');
				break;
			case 12:
				if (this.data.id.includes('team-members')) {
					tracking.trackEvent((expanded ? 'Expanding' : 'Collapsing') + ' Project Scheduling, Project Team');
				} else if (this.data.id.includes('phases')) {
					tracking.trackEvent((expanded ? 'Expanding' : 'Collapsing') + ' Project Scheduling, Milestone List');
				}
				break;
			case 22:
				tracking.trackEvent((expanded ? 'Expanding' : 'Collapsing') + ' People Scheduling, Person Group');
				break;
		}
		if (this.preventExpansion) return;
		this.expanded = expanded;
		if (this.onExpand) {
			this.onExpand(this.expanded);
		}
	}

	// Assigns the correct itemRow to each item, and assigns itemRow count to the group
	composeItemRows(items = []) {
		this.items = items;
		const itemRows = [];
		const itemsWithRows = [];
		const itemsWithoutRows = [];
		const itemRowPropName = this.groupIds ? 'groupedItemRow' : 'itemRow';
		for (const item of items) {
			if (item.filtered || (!item.visible && !item.isBeingDragged)) continue;
			if (item[itemRowPropName] !== undefined && item[itemRowPropName] >= 0) {
				itemsWithRows.push(item);
			} else {
				itemsWithoutRows.push(item);
			}
		}
		for (const item of itemsWithRows) {
			while (itemRows.length <= item[itemRowPropName]) {
				itemRows.push([]);
			}
			itemRows[item[itemRowPropName]].push(item);
		}
		for (const item of itemsWithoutRows) {
			const {startDate, endDate} = item;
			let needsNewRow = true;
			for (let i = 0; i < itemRows.length; i++) {
				let canAddToRow = true;
				for (const rowItem of itemRows[i]) {
					if (startDate < rowItem.endDate + 1 && rowItem.startDate <= endDate) {
						canAddToRow = false;
						break;
					}
				}
				if (canAddToRow) {
					itemRows[i].push(item);
					item[itemRowPropName] = i;
					needsNewRow = false;
					break;
				}
			}
			if (needsNewRow) {
				itemRows.push([item]);
				item[itemRowPropName] = itemRows.length - 1;
			}
		}
		while (itemRows.length > 1 && !itemRows[0].length) {
			itemRows.shift();
			for (const item of items) {
				item[itemRowPropName]--;
			}
		}
		this.itemRowCount = itemRows.length || 1;
		if (this.minimumItemRowCount && this.itemRowCount < this.minimumItemRowCount) {
			this.itemRowCount = this.minimumItemRowCount;
		}
	}

	adjustItemY(item, y) {
		return Math.floor(y + (this.itemRowHeight - 1 - item.height) / 2);
	}

	draw(x, y, width, height, group, collapsableSectionHeight) {
		//Only add the part of the group that is sitcking out from below the collapsable section if they overlap
		if (!group.isInCollapsableSection && y < collapsableSectionHeight) {
			interactionManager.addGroup(group, collapsableSectionHeight, group.height + y - collapsableSectionHeight);
		} else {
			interactionManager.addGroup(group, y, group.height);
		}
	}
}

export default Group;
